import {Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {Observable, forkJoin} from 'rxjs';
import {TuningPopularityStats, TopEcuBuildStats} from '../../models/dashboard-statistics.model';
import {DashboardStatisticsService} from '../../services/dashboard-statistics.service';
import {DashboardPeriodService, PeriodMode} from '../../../dashboard/services/dashboard-period.service';

export function tuningNamesFromRanking(label: string): string[] {
  return label.split(/\s+\+\s+/).map(name => name.trim()).filter(Boolean);
}

@Component({
  selector: 'app-top-rankings',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './top-rankings.component.html',
  styleUrls: ['./top-rankings.component.css']
})
export class TopRankingsComponent implements OnInit {

  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mode$: Observable<PeriodMode> = toObservable(this.periodService.mode);

  topTunings: TuningPopularityStats[] = [];
  topEcuBuilds: TopEcuBuildStats[] = [];
  isLoading = signal<boolean>(true);

  ngOnInit(): void {
    this.mode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadData();
    });
  }

  openTuningImports(tuning: TuningPopularityStats): void {
    const tuningNames = tuningNamesFromRanking(tuning.resellerProjectDetails);
    if (tuningNames.length === 0) return;

    this.router.navigate(['/app/file-service'], {
      queryParams: {
        tuningNames,
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd()
      }
    });
  }

  private loadData(): void {
    const start = this.periodService.currentStart();
    const end = this.periodService.currentEnd();
    this.isLoading.set(true);
    forkJoin({
      tunings: this.statsService.getTopTunings(start, end),
      ecuBuilds: this.statsService.getTopEcuBuilds(start, end)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({tunings, ecuBuilds}) => {
          this.topTunings = tunings;
          this.topEcuBuilds = ecuBuilds;
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  get maxTuningCount(): number {
    if (!this.topTunings || this.topTunings.length === 0) return 1;
    return Math.max(...this.topTunings.map(t => t.countRequest), 1);
  }

  get maxEcuBuildCount(): number {
    if (!this.topEcuBuilds || this.topEcuBuilds.length === 0) return 1;
    return Math.max(...this.topEcuBuilds.map(e => e.countRequest), 1);
  }

  getTuningBarWidth(count: number): number {
    return (count / this.maxTuningCount) * 100;
  }

  getEcuBuildBarWidth(count: number): number {
    return (count / this.maxEcuBuildCount) * 100;
  }
}
